import type { LucideIcon, LucideProps } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * Icon wrapper — enforces DS-wide stroke width + sized scale.
 *
 * - Stroke: 1.75 (sharp, sharper than lucide default 2)
 * - Sizes: xs 14 · sm 16 · md 18 · lg 20 · xl 24 · 2xl 32
 * - Color inherits from `currentColor` — use Tailwind text-* on the parent
 *
 * Usage:
 *   <Icon icon={Users} size="md" />
 */
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'

const SIZES: Record<IconSize, number> = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
  '2xl': 32,
}

export interface IconProps extends Omit<LucideProps, 'size' | 'ref'> {
  icon: LucideIcon
  size?: IconSize
}

export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  ({ icon: LucideIconComponent, size = 'md', strokeWidth = 1.75, className, ...props }, ref) => (
    <LucideIconComponent
      ref={ref}
      size={SIZES[size]}
      strokeWidth={strokeWidth}
      className={cn('shrink-0', className)}
      aria-hidden="true"
      focusable="false"
      {...props}
    />
  ),
)
Icon.displayName = 'Icon'
